import { ITEM_TYPES } from '../constants';
import { annualAmount, debtBalanceAt, debtPaymentDue } from './engine';

// Default settings used when not provided by user preferences.
export const FIRE_DEFAULTS = Object.freeze({
    withdrawalRate: 0.04,
    horizonYears: 40,
});

/**
 * Effective "count this asset toward the FIRE number" flag, with type defaults.
 * Legacy items lack the field, so we fall back to a sensible default per type:
 *   - investments: included
 *   - real estate: included by default (the user unticks a home they live in)
 *   - everything else (income/expense/loan): not an asset
 */
export const getFireInclusion = (item) => {
    if (!item) return false;
    if (typeof item.includeInFire === 'boolean') return item.includeInFire;
    switch (item.type) {
        case ITEM_TYPES.OTHER_INVESTMENT: return true;
        case ITEM_TYPES.REAL_ESTATE: return true;
        default: return false;
    }
};

/**
 * Effective "this income is passive" flag, with type defaults.
 *   - income items: not passive by default (salary); user opts in (pension, royalties)
 *   - real estate: rental income is passive by default
 *   - investments: yield is passive by default when the item produces income
 * Liquid-asset interest is always passive and handled directly in the engine.
 */
export const getFirePassive = (item) => {
    if (!item) return false;
    if (typeof item.isPassiveIncome === 'boolean') return item.isPassiveIncome;
    switch (item.type) {
        case ITEM_TYPES.REAL_ESTATE: return !!item.isRented;
        case ITEM_TYPES.OTHER_INVESTMENT: return !!item.isIncome;
        default: return false; // income (salary), expense, loan
    }
};

/**
 * Collapse a boolean-per-month series into contiguous [startIdx, endIdx] zones.
 * @param {boolean[]} flags
 * @returns {{startIdx:number, endIdx:number}[]}
 */
export const computeFIZones = (flags) => {
    const zones = [];
    if (!Array.isArray(flags)) return zones;
    let start = -1;
    for (let i = 0; i < flags.length; i++) {
        if (flags[i]) {
            if (start === -1) start = i;
        } else if (start !== -1) {
            zones.push({ startIdx: start, endIdx: i - 1 });
            start = -1;
        }
    }
    if (start !== -1) zones.push({ startIdx: start, endIdx: flags.length - 1 });
    return zones;
};

const addMonths = (baseDate, months) =>
    new Date(baseDate.getFullYear(), baseDate.getMonth() + months, baseDate.getDate());

// Build the per-view metrics (zones, first/stable dates, current state) from a boolean flag key.
const buildViewMetrics = (projection, flagKey, baseDate) => {
    const flags = projection.map(p => !!p[flagKey]);
    const zones = computeFIZones(flags);
    const firstFIIndex = flags.findIndex(Boolean);

    // stableFromIndex = first index from which the flag stays true to the end of the horizon.
    let stableFromIndex = -1;
    if (flags.length > 0 && flags[flags.length - 1]) {
        stableFromIndex = flags.length - 1;
        for (let i = flags.length - 1; i >= 0 && flags[i]; i--) {
            stableFromIndex = i;
        }
    }

    const firstFIDate = firstFIIndex >= 0 ? addMonths(baseDate, firstFIIndex) : null;
    const stableFromDate = stableFromIndex >= 0 ? addMonths(baseDate, stableFromIndex) : null;
    const isCurrentlyFI = flags.length > 0 ? flags[0] : false;
    const timeToFI = firstFIIndex >= 0
        ? { years: Math.floor(firstFIIndex / 12), months: firstFIIndex % 12 }
        : null;

    return { zones, firstFIIndex, firstFIDate, stableFromIndex, stableFromDate, isCurrentlyFI, timeToFI };
};

const emptyView = () => ({
    zones: [], firstFIIndex: -1, firstFIDate: null, stableFromIndex: -1,
    stableFromDate: null, isCurrentlyFI: false, timeToFI: null,
});

/**
 * Top-level pure aggregator. Consumes the extended projection array produced by
 * generateProjectionData (which carries the additive FIRE fields) plus settings.
 *
 * @param {Array} projection
 * @param {Object} opts
 * @param {number} [opts.withdrawalRate=0.04]
 * @param {number} [opts.incomeT0Annual=0]  gross annual income at t0 (for savings rate)
 * @param {Date}   [opts.baseDate=new Date()]  anchor for first-FI / time-to-FI
 */
export const computeFireMetrics = (projection, opts = {}) => {
    const { withdrawalRate = FIRE_DEFAULTS.withdrawalRate, incomeT0Annual = 0, baseDate = new Date(), coastTargetYear = null } = opts;

    if (!Array.isArray(projection) || projection.length === 0) {
        return {
            asset: { ...emptyView(), currentProgress: 0 },
            income: { ...emptyView(), currentCoverage: 0 },
            currentFireNumber: 0,
            currentAssets: 0, currentExpenses: 0, currentPassiveIncome: 0, currentIncomeExpenses: 0,
            milestones: null,
            coast: null,
            savingsRate: null,
            withdrawalRate,
        };
    }

    const asset = buildViewMetrics(projection, 'isFIAsset', baseDate);
    const income = buildViewMetrics(projection, 'isFIIncome', baseDate);
    // Zones where the post-FI drawdown balance still covers the FIRE number (for the drawdown chart toggle).
    asset.drawdownZones = computeFIZones(projection.map(p => !!p.isFIAssetDrawdown));

    const t0 = projection[0];
    const reqAnnualT0 = t0.requiredExpensesNominal ?? 0;
    const fireAssetsT0 = t0.fireAssetsNominal ?? 0;
    const passiveT0 = t0.passiveIncomeNominal ?? 0;
    const incomeReqAnnualT0 = t0.incomeRequiredExpensesNominal ?? reqAnnualT0; // income view: full debt payment

    // Asset progress: assets / FIRE number at t0, clamped to [0,1].
    let currentProgress;
    if (reqAnnualT0 <= 0) {
        currentProgress = 1; // no expenses to cover -> trivially funded
    } else if (withdrawalRate <= 0) {
        currentProgress = 0; // undefined FIRE number
    } else {
        const fireNumberT0 = reqAnnualT0 / withdrawalRate;
        currentProgress = fireNumberT0 > 0 ? fireAssetsT0 / fireNumberT0 : 0;
    }
    asset.currentProgress = Math.max(0, Math.min(1, currentProgress));

    // Income coverage: passive income / expenses at t0, clamped to [0,1].
    income.currentCoverage = incomeReqAnnualT0 <= 0 ? 1 : Math.max(0, Math.min(1, passiveT0 / incomeReqAnnualT0));

    // Display FIRE number (today's-money basis already applied to the row by the engine).
    let currentFireNumber = 0;
    if (typeof t0.fireNumber === 'number' && isFinite(t0.fireNumber)) {
        currentFireNumber = t0.fireNumber;
    } else if (withdrawalRate > 0 && (t0.requiredExpenses ?? 0) > 0) {
        currentFireNumber = t0.requiredExpenses / withdrawalRate;
    }

    // Savings rate = share of gross income not consumed (expenses exclude contributions & debt principal).
    // May be negative when expenses exceed income; null when income is unavailable.
    const savingsRate = incomeT0Annual > 0 ? (incomeT0Annual - reqAnnualT0) / incomeT0Annual : null;

    // Milestone tiers (asset method): Lean / FIRE / Fat as multiples of the FIRE number.
    const milestones = (withdrawalRate > 0 && currentFireNumber > 0) ? {
        lean: 0.7 * currentFireNumber,
        fire: currentFireNumber,
        fat: 1.5 * currentFireNumber,
        current: fireAssetsT0,
    } : null;

    // Coast FIRE: if you stop contributing today, do current investable assets grow into the FIRE number
    // by the target year? Grow current assets at the t0 blended return; compare to the FIRE number at the
    // target year. Both sides use only t0 values plus a fixed one-year expense-growth rate, so the result
    // is independent of the chart horizon (which only controls how far the projection array is plotted).
    let coast = null;
    const blended = t0.blendedReturnNominal;
    if (coastTargetYear && typeof blended === 'number' && withdrawalRate > 0 && reqAnnualT0 > 0) {
        const years = Math.max(0, coastTargetYear - baseDate.getFullYear());
        // Annual expense growth from the first 12 months (month 12 exists for any horizon), extrapolated
        // to the target year — never index the horizon-limited tail, which would make this depend on it.
        const exp12 = projection.length > 12 ? (projection[12].requiredExpensesNominal ?? reqAnnualT0) : reqAnnualT0;
        const expenseGrowth = reqAnnualT0 > 0 ? (exp12 / reqAnnualT0) - 1 : 0;
        const expensesAtTarget = reqAnnualT0 * Math.pow(1 + expenseGrowth, years);
        const fireNumberAtTarget = expensesAtTarget / withdrawalRate;
        const balanceAtTarget = fireAssetsT0 * Math.pow(1 + blended, years);
        coast = {
            targetYear: coastTargetYear,
            reached: fireAssetsT0 > 0 && balanceAtTarget >= fireNumberAtTarget,
            ratio: fireNumberAtTarget > 0 ? Math.max(0, balanceAtTarget / fireNumberAtTarget) : 0,
            withinHorizon: years * 12 <= projection.length - 1,
        };
    }

    return {
        asset, income, currentFireNumber, savingsRate, withdrawalRate,
        currentAssets: fireAssetsT0,
        currentExpenses: reqAnnualT0,
        currentPassiveIncome: passiveT0,
        currentIncomeExpenses: incomeReqAnnualT0,
        milestones,
        coast,
    };
};

/**
 * Current (t0) breakdown of what counts toward FIRE expenses and passive income.
 * Pure; mirrors the engine's t0 rules (interest-only debt, savings excluded, type-default flags).
 * Amounts are annual, in today's money. Line items carry { name | key, isInterest?, amount }.
 *
 * Asset rows also carry `itemId` and `included`, and an *excluded* item still
 * gets a row. That is what lets the breakdown be the control rather than a
 * read-out: a list that only shows what counts cannot show you what does not,
 * so there is nothing to switch back on. `assetsTotal` sums the included rows
 * only, so no figure changes — the array simply stops hiding half the answer.
 */
export const computeFireBreakdown = (items, liquidAssets, liquidAssetsInterestRate, dashboardSettings = {}) => {
    const commonExpenses = [];   // living + non-rented property costs (shared by both views)
    const debtFullLines = [];    // full debt payment (income view only)
    const passiveIncome = [];
    const assets = [];

    const liq = parseFloat(liquidAssets) || 0;
    const liqRate = parseFloat(liquidAssetsInterestRate) || 0;
    if (liq !== 0) {
        assets.push({ key: 'liquid', amount: liq, included: true });
    }
    if (liq > 0 && liqRate > 0) {
        passiveIncome.push({ key: 'liquid', amount: liq * liqRate });
    }

    (items || []).forEach((item) => {
        const name = item.name || '';
        switch (item.type) {
            case ITEM_TYPES.REAL_ESTATE: {
                const mortgageBalance = item.hasMortgage ? (debtBalanceAt(item) || 0) : 0;
                assets.push({
                    itemId: item.id, name, isEquity: true,
                    amount: (parseFloat(item.currentValue) || 0) - mortgageBalance,
                    included: getFireInclusion(item),
                });
                if (item.isRented) {
                    if (getFirePassive(item)) {
                        const rent = annualAmount(item.monthlyRentalIncome, item.amountFrequency);
                        if (rent !== 0) passiveIncome.push({ name, amount: rent });
                    }
                } else {
                    const costs = annualAmount(item.monthlyCosts, item.amountFrequency);
                    if (costs !== 0) commonExpenses.push({ name, amount: costs });
                }
                const mortgagePayment = debtPaymentDue(item) * 12;
                if (mortgagePayment > 0) debtFullLines.push({ name, isPayment: true, amount: mortgagePayment });
                break;
            }
            case ITEM_TYPES.OTHER_INVESTMENT: {
                const val = parseFloat(item.currentValue) || 0;
                if (val !== 0) assets.push({ itemId: item.id, name, amount: val, included: getFireInclusion(item) });
                if (item.isIncome && getFirePassive(item)) {
                    const yieldAmt = (parseFloat(item.currentValue) || 0) * (parseFloat(item.annualIncomeYield) || 0);
                    if (yieldAmt !== 0) passiveIncome.push({ name, amount: yieldAmt });
                }
                break;
            }
            case ITEM_TYPES.EXPENSE: {
                const exp = annualAmount(item.monthlyExpense, item.amountFrequency);
                if (exp !== 0) commonExpenses.push({ name, amount: exp });
                break;
            }
            case ITEM_TYPES.LOAN: {
                const loanBalance = debtBalanceAt(item) || 0;
                if (loanBalance > 0) {
                    assets.push({ itemId: item.id, name, isLoan: true, amount: -loanBalance, included: true }); // net worth: deduct the balance
                }
                const loanPayment = debtPaymentDue(item) * 12;
                if (loanPayment > 0) debtFullLines.push({ name, isPayment: true, amount: loanPayment });
                break;
            }
            case ITEM_TYPES.INCOME: {
                if (getFirePassive(item)) {
                    const inc = annualAmount(item.monthlyIncome, item.amountFrequency);
                    if (inc !== 0) passiveIncome.push({ name, amount: inc });
                }
                break;
            }
            default: break;
        }
    });

    const overrideEnabled = !!dashboardSettings.fireExpenseOverrideEnabled;
    const overrideAmount = parseFloat(dashboardSettings.fireExpenseOverrideAmount) || 0;
    const expenses = [...commonExpenses];                          // asset / FIRE view: debt is netted out of assets, so no debt service here
    const incomeExpenses = [...commonExpenses, ...debtFullLines];  // income view (full debt payment until payoff)
    const derivedExpensesTotal = expenses.reduce((sum, e) => sum + e.amount, 0);
    const debtPaymentsTotal = debtFullLines.reduce((sum, e) => sum + e.amount, 0);
    const passiveIncomeTotal = passiveIncome.reduce((sum, p) => sum + p.amount, 0);
    const assetsTotal = assets.reduce((sum, a) => sum + (a.included ? a.amount : 0), 0);
    // The override replaces only the LIVING expenses; the income view always adds full debt payments on top.
    const livingExpensesTotal = overrideEnabled ? overrideAmount : derivedExpensesTotal;

    return {
        expenses,
        incomeExpenses,
        debtFullLines,
        passiveIncome,
        assets,
        derivedExpensesTotal,
        debtPaymentsTotal,
        expensesTotal: livingExpensesTotal,
        incomeExpensesTotal: livingExpensesTotal + debtPaymentsTotal,
        passiveIncomeTotal,
        assetsTotal,
        overrideEnabled,
        overrideAmount,
    };
};
